const SAME_ORIGIN = new Set(["", "same-origin", "same_origin", "/"]);

/** True when API calls should use the page origin (nginx/dev proxy handles upstream). */
export function isSameOriginGatewayMode(raw: string): boolean {
  return SAME_ORIGIN.has(raw.trim().toLowerCase());
}

/**
 * API gateway base URL from Create React App env — single place, no hardcoded hosts.
 * Accepts the same variables commonly used across this repo.
 * Use `same-origin` (or leave empty) so nginx/dev proxy forwards to the real gateway.
 */
export function gatewayBaseUrlFromEnv(): string {
  const raw = (
    process.env.REACT_APP_API_GATEWAY_URL ||
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    ""
  ).trim();
  if (isSameOriginGatewayMode(raw)) {
    return "";
  }

  // Stale builds often pointed at api-gateway-jwt (:4701) which rejects Keycloak tokens.
  let url = raw;
  if (/localhost:4701\b/.test(url)) {
    url = url.replace(/localhost:4701\b/g, "localhost:4702");
  }

  return url.replace(/\/$/, "");
}

/** Browser origin when env is `same-origin` (nginx/dev proxy forwards to the real gateway). */
export function resolveGatewayBaseUrl(options?: {
  allowLocalFallback?: boolean;
}): string {
  const fromEnv = gatewayBaseUrlFromEnv();
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  if (options?.allowLocalFallback) {
    return "http://localhost:3700";
  }
  return "";
}

/** Login / select-app / SSO — always the real gateway (never SPA same-origin; nginx does not proxy /login). */
export function resolveGatewayLoginApiBase(): string {
  const loginUrl = (process.env.REACT_APP_LOGIN_URL ?? "").trim();
  if (loginUrl) {
    try {
      return new URL(loginUrl).origin;
    } catch {
      // fall through
    }
  }
  return resolveGatewayUpstreamHttpUrl();
}

/**
 * Absolute gateway HTTP origin used for WebSockets / shared-dashboards.
 * Prefer REACT_APP_GATEWAY_UPSTREAM when HTTP is same-origin — WS upgrades through
 * the SPA host often fail until nginx `/ws` is deployed.
 */
export function resolveGatewayUpstreamHttpUrl(): string {
  const upstream = (process.env.REACT_APP_GATEWAY_UPSTREAM ?? "").trim();
  if (upstream && !isSameOriginGatewayMode(upstream)) {
    const withScheme =
      upstream.startsWith("http://") || upstream.startsWith("https://")
        ? upstream
        : `https://${upstream}`;
    return withScheme.replace(/\/$/, "");
  }
  return resolveGatewayBaseUrl({ allowLocalFallback: true });
}

/** WebSocket gateway origin, e.g. `wss://api.edge.variphi.com`. */
export function resolveWebSocketGatewayOrigin(): string {
  const wsEnv = (process.env.REACT_APP_WS_GATEWAY_URL ?? "").trim();
  if (wsEnv && !isSameOriginGatewayMode(wsEnv)) {
    return wsEnv
      .replace(/^https/i, "wss")
      .replace(/^http/i, "ws")
      .replace(/\/$/, "");
  }
  const http = resolveGatewayUpstreamHttpUrl();
  if (!http) {
    if (typeof window !== "undefined") {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${proto}//${window.location.host}`;
    }
    return "ws://localhost:4702";
  }
  return http.replace(/^https/i, "wss").replace(/^http/i, "ws").replace(/\/$/, "");
}

function applicationIdFromEnv(): string {
  const applicationId = (process.env.REACT_APP_APPLICATION_ID ?? "").trim();
  if (!applicationId) {
    throw new Error(
      "Set REACT_APP_APPLICATION_ID in your environment (e.g. `.env`).",
    );
  }
  return applicationId;
}

/**
 * Values passed into `@variphigen/shared-dashboards` — read from env only at the app shell.
 * Uses the real gateway upstream so GpuDetails WS hits
 * `wss://api.edge.variphi.com/ws/gpu-status` (not the SPA host).
 */
export function loadSharedDashboardConfig(): {
  apiGatewayUrl: string;
  applicationId: string;
} {
  return {
    apiGatewayUrl: resolveGatewayUpstreamHttpUrl(),
    applicationId: applicationIdFromEnv(),
  };
}

/** Cleaning metrics API — dedicated URL if set, otherwise the main gateway. */
export function cleaningApiBaseUrlFromEnv(): string {
  const dedicated = (process.env.REACT_APP_CLEANING_API_URL ?? "").trim();
  if (dedicated) return dedicated.replace(/\/$/, "");
  return gatewayBaseUrlFromEnv();
}
